import React, { useState } from 'react';
import { X, User, Clock, SkipForward, ArrowRightLeft, FileText, History } from 'lucide-react';
import type { TaskDetailItem, TaskReassignPayload, TaskSkipPayload, TaskSLAAdjustPayload } from '../../shared/types/task';
import type { TaskMetadata } from '../../shared/types/task';

interface GestaoTaskDrawerProps {
  task: TaskDetailItem | null;
  isLoading: boolean;
  logs: any[];
  logsLoading: boolean;
  onClose: () => void;
  onReassign: (payload: TaskReassignPayload) => Promise<void>;
  onSkip: (payload: TaskSkipPayload) => Promise<void>;
  onAdjustSLA: (payload: TaskSLAAdjustPayload) => Promise<void>;
  isReassigning: boolean;
  isSkipping: boolean;
  isAdjustingSLA: boolean;
  currentUserId: string;
  onOpenExecution?: (executionId: string) => void;
}

export const GestaoTaskDrawer: React.FC<GestaoTaskDrawerProps> = ({
  task,
  isLoading,
  logs,
  logsLoading,
  onClose,
  onReassign,
  onSkip,
  onAdjustSLA,
  isReassigning,
  isSkipping,
  isAdjustingSLA,
  currentUserId,
  onOpenExecution,
}) => {
  const [activeAction, setActiveAction] = useState<'reassign' | 'skip' | 'sla' | null>(null);
  const [actionInput, setActionInput] = useState('');
  const [newAssignee, setNewAssignee] = useState('');

  if (!task) return null;

  const meta = task.metadata as TaskMetadata | null;
  const canSkip = meta?.rules?.canSkip ?? false;

  const handleAction = async () => {
    if (!actionInput.trim()) return;

    if (activeAction === 'reassign' && newAssignee.trim()) {
      await onReassign({
        taskId: task.id,
        newAssignee: newAssignee.trim(),
        justification: actionInput.trim(),
        performedBy: currentUserId,
      });
    } else if (activeAction === 'skip') {
      await onSkip({
        taskId: task.id,
        justification: actionInput.trim(),
        performedBy: currentUserId,
      });
    } else if (activeAction === 'sla') {
      await onAdjustSLA({
        taskId: task.id,
        newDeadline: newAssignee, // reuse field for date
        justification: actionInput.trim(),
        performedBy: currentUserId,
      });
    }

    setActiveAction(null);
    setActionInput('');
    setNewAssignee('');
  };

  return (
    <div className="w-[400px] shrink-0 bg-white dark:bg-[var(--bg-surface)] border-l border-slate-200 dark:border-slate-800 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
        <div className="min-w-0">
          <p className="text-[10px] font-mono text-slate-400 truncate">{task.referenceCode}</p>
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">
            {task.stageName}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center text-slate-400 animate-pulse">
          Carregando...
        </div>
      ) : (
        <>
          {/* Task info */}
          <div className="px-5 py-4 space-y-3 border-b border-slate-100 dark:border-slate-800">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-slate-400 dark:text-slate-500">Status</span>
                <p className="font-medium text-slate-700 dark:text-slate-300 mt-0.5">{task.status}</p>
              </div>
              <div>
                <span className="text-slate-400 dark:text-slate-500">Prioridade</span>
                <p className="font-medium text-slate-700 dark:text-slate-300 mt-0.5 uppercase">{task.priority ?? '-'}</p>
              </div>
              <div>
                <span className="text-slate-400 dark:text-slate-500">Responsavel</span>
                <p className="font-medium text-slate-700 dark:text-slate-300 mt-0.5">
                  {task.assignedToName ?? task.assignedTo?.slice(0, 8) ?? 'Nao atribuido'}
                </p>
              </div>
              <div>
                <span className="text-slate-400 dark:text-slate-500">SLA</span>
                <p className={`font-medium mt-0.5 ${task.isOverdue ? 'text-red-600' : 'text-slate-700 dark:text-slate-300'}`}>
                  {task.slaDeadline ? new Date(task.slaDeadline).toLocaleString('pt-BR') : '-'}
                </p>
              </div>
              <div>
                <span className="text-slate-400 dark:text-slate-500">Tipo Conclusao</span>
                <p className="font-medium text-slate-700 dark:text-slate-300 mt-0.5">
                  {task.completionType === 'approve_reject' ? 'Aprovacao' : 'Execucao'}
                </p>
              </div>
              <div>
                <span className="text-slate-400 dark:text-slate-500">Papel</span>
                <p className="font-medium text-slate-700 dark:text-slate-300 mt-0.5">{meta?.role ?? '-'}</p>
              </div>
            </div>
            {onOpenExecution && (
              <button
                type="button"
                onClick={() => onOpenExecution(task.instanceId)}
                className="text-xs font-medium text-blue-600 hover:underline"
              >
                Abrir execução desta tarefa
              </button>
            )}

            {/* SOP preview */}
            {task.instruction && (
              <div className="mt-3">
                <div className="flex items-center gap-1 text-xs text-slate-400 mb-1">
                  <FileText size={10} /> SOP
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-3">
                  {task.instruction}
                </p>
              </div>
            )}
          </div>

          {/* Management actions */}
          <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
              Acoes de Gestao
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setActiveAction(activeAction === 'reassign' ? null : 'reassign')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                <ArrowRightLeft size={12} /> Reatribuir
              </button>
              {canSkip && (
                <button
                  onClick={() => setActiveAction(activeAction === 'skip' ? null : 'skip')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                >
                  <SkipForward size={12} /> Pular
                </button>
              )}
              <button
                onClick={() => setActiveAction(activeAction === 'sla' ? null : 'sla')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <Clock size={12} /> Ajustar SLA
              </button>
            </div>

            {/* Action form */}
            {activeAction && (
              <div className="mt-3 space-y-2">
                {activeAction === 'reassign' && (
                  <input
                    type="text"
                    value={newAssignee}
                    onChange={(e) => setNewAssignee(e.target.value)}
                    placeholder="ID ou email do novo responsavel..."
                    className="w-full text-xs px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                  />
                )}
                {activeAction === 'sla' && (
                  <input
                    type="datetime-local"
                    value={newAssignee}
                    onChange={(e) => setNewAssignee(e.target.value)}
                    className="w-full text-xs px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                  />
                )}
                <input
                  type="text"
                  value={actionInput}
                  onChange={(e) => setActionInput(e.target.value)}
                  placeholder="Justificativa obrigatoria..."
                  className="w-full text-xs px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                />
                <button
                  onClick={handleAction}
                  disabled={!actionInput.trim() || isReassigning || isSkipping || isAdjustingSLA}
                  className="px-4 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {(isReassigning || isSkipping || isAdjustingSLA) ? 'Processando...' : 'Confirmar'}
                </button>
              </div>
            )}
          </div>

          {/* Logs / History */}
          <div className="flex-1 overflow-y-auto px-5 py-3">
            <div className="flex items-center gap-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
              <History size={10} /> Historico
            </div>
            {logsLoading ? (
              <p className="text-xs text-slate-400 animate-pulse">Carregando...</p>
            ) : logs.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Nenhum registro.</p>
            ) : (
              <div className="space-y-2">
                {logs.map((log: any, idx: number) => (
                  <div key={log.id ?? idx} className="border-l-2 border-slate-200 dark:border-slate-700 pl-3 py-1">
                    <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{log.action}</span>
                    {log.justification && (
                      <p className="text-[10px] text-slate-400 mt-0.5">{log.justification}</p>
                    )}
                    <time className="text-[10px] text-slate-300">
                      {new Date(log.created_at).toLocaleString('pt-BR')}
                    </time>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
